import type { InboxSectionKey } from '../inbox-sections';
import type { InboxPrStatus, InboxPullRequest, InboxStackContext } from '../types';
import type { GroupedPullRequests, PrStackInfo } from './pull-requests';

import { INBOX_SECTIONS, STACK_SECTION_PRIORITY } from '../inbox-sections';

type PullRequestGroups = Omit<GroupedPullRequests, 'globalStackInfoMap'>;

type StackUnit = {
  pullRequests: InboxPullRequest[];
  sortTimestamp: number;
};

type CategorizePullRequest = (pr: InboxPullRequest, currentUser: string) => InboxSectionKey;

type GroupStackedPullRequestsParams = {
  prs: InboxPullRequest[];
  currentUser: string;
  stackContexts: InboxStackContext[];
  categorize: CategorizePullRequest;
  getPrId: (pr: InboxPullRequest) => string;
};

function getSortTimestamp(timestamp: string) {
  const parsedTimestamp = Date.parse(timestamp);
  return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
}

/** A PR's own status, which its row shows even when its stack sits in another section. */
function getIndividualStatus(
  pr: InboxPullRequest,
  currentUser: string,
  categorize: CategorizePullRequest,
): InboxPrStatus {
  if (pr.state === 'closed' && !pr.mergedAt) {
    return 'closed';
  }

  return categorize(pr, currentUser);
}

function getStackOwningCategory(
  pullRequests: InboxPullRequest[],
  currentUser: string,
  categorize: CategorizePullRequest,
) {
  const categories = new Set(pullRequests.map((pr) => categorize(pr, currentUser)));

  return STACK_SECTION_PRIORITY.find((category) => categories.has(category)) ?? 'merged';
}

function createUnitsByCategory() {
  return Object.fromEntries(INBOX_SECTIONS.map(({ key }) => [key, [] as StackUnit[]])) as Record<
    InboxSectionKey,
    StackUnit[]
  >;
}

function addStackUnit(
  unitsByCategory: Record<InboxSectionKey, StackUnit[]>,
  category: InboxSectionKey,
  pullRequests: InboxPullRequest[],
) {
  const timestampField = category === 'merged' ? 'updatedAt' : 'createdAt';
  unitsByCategory[category].push({
    pullRequests,
    sortTimestamp: Math.max(...pullRequests.map((pr) => getSortTimestamp(pr[timestampField]))),
  });
}

function addStackContext(params: {
  context: InboxStackContext;
  currentUser: string;
  seedPrsById: Map<string, InboxPullRequest>;
  consumedPrIds: Set<string>;
  stackInfoByPrId: Map<string, PrStackInfo>;
  unitsByCategory: Record<InboxSectionKey, StackUnit[]>;
  categorize: CategorizePullRequest;
  getPrId: GroupStackedPullRequestsParams['getPrId'];
}) {
  const { context, consumedPrIds, getPrId, seedPrsById } = params;
  const members = [...context.members].sort((left, right) => left.position - right.position);
  const hasVisibleSeed = members.some((member) => seedPrsById.has(getPrId(member.pullRequest)));
  const overlapsConsumedStack = members.some((member) =>
    consumedPrIds.has(getPrId(member.pullRequest)),
  );
  if (!hasVisibleSeed || overlapsConsumedStack || members.length < 2) {
    return;
  }

  const pullRequests = members.map(
    (member) => seedPrsById.get(getPrId(member.pullRequest)) ?? member.pullRequest,
  );
  members.forEach((member, index) => {
    const pr = pullRequests[index];
    if (!pr) {
      return;
    }

    const prId = getPrId(pr);
    consumedPrIds.add(prId);
    params.stackInfoByPrId.set(prId, {
      stackPosition: index + 1,
      stackTotal: pullRequests.length,
      stackDepth: member.depth,
      isPartOfStack: true,
      stackId: context.id,
      individualStatus: getIndividualStatus(pr, params.currentUser, params.categorize),
    });
  });

  addStackUnit(
    params.unitsByCategory,
    getStackOwningCategory(pullRequests, params.currentUser, params.categorize),
    pullRequests,
  );
}

function addStandalonePullRequests(params: {
  prs: InboxPullRequest[];
  currentUser: string;
  consumedPrIds: Set<string>;
  stackInfoByPrId: Map<string, PrStackInfo>;
  unitsByCategory: Record<InboxSectionKey, StackUnit[]>;
  categorize: CategorizePullRequest;
  getPrId: GroupStackedPullRequestsParams['getPrId'];
}) {
  for (const pr of params.prs) {
    const prId = params.getPrId(pr);
    if (params.consumedPrIds.has(prId)) {
      continue;
    }

    addStackUnit(params.unitsByCategory, params.categorize(pr, params.currentUser), [pr]);
    params.stackInfoByPrId.set(prId, {
      stackPosition: 1,
      stackTotal: 1,
      stackDepth: 0,
      isPartOfStack: false,
      individualStatus: getIndividualStatus(pr, params.currentUser, params.categorize),
    });
  }
}

function flattenUnits(unitsByCategory: Record<InboxSectionKey, StackUnit[]>) {
  return Object.fromEntries(
    INBOX_SECTIONS.map(({ key }) => [
      key,
      unitsByCategory[key]
        .sort((left, right) => right.sortTimestamp - left.sortTimestamp)
        .flatMap((unit) => unit.pullRequests),
    ]),
  ) as PullRequestGroups;
}

export function groupStackedPullRequests(
  params: GroupStackedPullRequestsParams,
): GroupedPullRequests {
  const stackInfoByPrId = new Map<string, PrStackInfo>();
  const unitsByCategory = createUnitsByCategory();
  const seedPrsById = new Map(params.prs.map((pr) => [params.getPrId(pr), pr]));
  const consumedPrIds = new Set<string>();

  params.stackContexts.forEach((context) =>
    addStackContext({
      ...params,
      context,
      consumedPrIds,
      seedPrsById,
      stackInfoByPrId,
      unitsByCategory,
    }),
  );
  addStandalonePullRequests({
    ...params,
    consumedPrIds,
    stackInfoByPrId,
    unitsByCategory,
  });

  return {
    ...flattenUnits(unitsByCategory),
    globalStackInfoMap: stackInfoByPrId,
  };
}
